import React, { useState, useEffect } from "react";
import "./Reports.css";
import { useComplaints } from "../../hooks/useComplaints";
import { useToast } from "../../mesageToast/ToastContainer";

const Reports = () => {
  const [mainTab, setMainTab] = useState("complaint");
  const [activeTab, setActiveTab] = useState("raised");
  const [itemPopup, setItemPopup] = useState({ show: false, items: [] });
  const addToast = useToast();
  const { complaints, loading, error, getComplaints, clearComplaintError } = useComplaints();

  useEffect(() => {
    getComplaints({ status: "raised" });
  }, [getComplaints]);

  useEffect(() => {
    if (error) {
      addToast("Error", error, "error");
      clearComplaintError();
    }
  }, [error, addToast, clearComplaintError]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    if (tab === "raised") {
      getComplaints({ status: "raised" });
    } else if (tab === "resolved") {
      getComplaints({ status: "resolved" });
    } else {
      getComplaints();
    }
  };

  const calculateTimeTaken = (createdAt, resolvedAt) => {
    if (!resolvedAt) return "N/A";
    const created = new Date(createdAt);
    const resolved = new Date(resolvedAt);
    const diffMs = resolved - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);
    return `${diffHours}h ${diffMins}m ${diffSecs}s`;
  };

  const getResolvedTime = (complaint) => {
    const resolvedHistory = complaint.history?.find(h => h.status === "resolved");
    return resolvedHistory ? resolvedHistory.timestamp : null;
  };

  const renderTableHeaders = () => {
    const commonHeaders = [
      "Case No",
      "Complaint ID", 
      "Restaurant Name",
      "Location",
      "Created At",
      "Items & Price",
      "Total Item Price",
      "Order Value",
      "Order Status",
      "Resolution Status"
    ];

    if (activeTab === "resolved") {
      return [...commonHeaders, "Support Team Message", "Time Taken"];
    }
    return commonHeaders;
  };

  const renderTableRow = (complaint, index) => {
    const resolvedTime = getResolvedTime(complaint);
    const timeTaken = calculateTimeTaken(complaint.createdAt, resolvedTime);
    const supportMessage = complaint.message?.find(m => m.userType === "support team")?.content || "N/A";

    return (
      <tr key={complaint._id || index}>
        <td style={{whiteSpace: "nowrap"}}>{complaint.operationId}</td>
        <td style={{whiteSpace: "nowrap"}}>{complaint._id}</td>
        <td>{complaint.restaurant || "N/A"}</td>
        <td>{complaint.location || "N/A"}</td>
        <td>{new Date(complaint.createdAt).toLocaleDateString()}</td>
        <td>
          {complaint.selectitem?.length > 0 ? (
            <button 
              className="view-btn"
              onClick={() => setItemPopup({ show: true, items: complaint.selectitem })}
            >
              Show
            </button>
          ) : "N/A"}
        </td>
        <td>₹{complaint.selectitem?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0}</td>
        <td>₹{complaint.price || 0}</td>
        <td>{complaint.orderStatus || "N/A"}</td>
        <td><span className="status-badge">{complaint.status}</span></td>
        {activeTab === "resolved" && (
          <>
            <td>{supportMessage}</td>
            <td>{timeTaken}</td>
          </>
        )}
      </tr>
    );
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h2>Reports</h2>
      </div>
      
      <div className="main-tabs">
        <button
          className={`main-tab-btn ${mainTab === "complaint" ? "active" : ""}`}
          onClick={() => setMainTab("complaint")}
        >
          Complaint
        </button>
      </div>

      {mainTab === "complaint" && (
        <>
          <div className="complaint-tabs">
            <button
              className={`tab-btn ${activeTab === "raised" ? "active" : ""}`}
              onClick={() => handleTabChange("raised")}
            >
              Raised
            </button>
            <button
              className={`tab-btn ${activeTab === "inprogress" ? "active" : ""}`}
              onClick={() => handleTabChange("inprogress")}
            >
              In Progress
            </button>
            <button
              className={`tab-btn ${activeTab === "resolved" ? "active" : ""}`}
              onClick={() => handleTabChange("resolved")}
            >
              Resolved
            </button>
          </div>

          <div className="complaint-content">
            {loading ? (
              <div className="loading">Loading complaints...</div>
            ) : activeTab === "inprogress" ? (
              <div className="no-data">No in progress complaints available</div>
            ) : (
              <div className="table-container">
                <table className="complaint-table">
                  <thead>
                    <tr>
                      {renderTableHeaders().map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {complaints.length > 0 ? (
                      complaints.map((complaint, index) => 
                        renderTableRow(complaint, index)
                      )
                    ) : (
                      <tr>
                        <td colSpan={renderTableHeaders().length} className="no-data">
                          No {activeTab} complaints found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {itemPopup.show && (
        <div className="modal-overlay" onClick={() => setItemPopup({ show: false, items: [] })}>
          <div className="complaint-modal" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Complaint Item Details</h3>
              <button className="close-btn" onClick={() => setItemPopup({ show: false, items: [] })}>×</button>
            </div>
            <div className="order-items">
              {itemPopup.items.map((item, idx) => (
                <div key={idx} className="item-detail" style={{display:"flex", justifyContent: "space-between"}}>
                  <span className="item-name">{item.name}</span>
                  <span className="item-qty">Qty: {item.quantity}</span>
                  <span className="item-price">₹{item.price || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;